import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatRadioChange } from '@angular/material/radio';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { SwalComponent } from '@sweetalert2/ngx-sweetalert2';
import { take } from 'rxjs/operators';

import { ICustomer } from '@ruby/modules/customer-select/models/customer.interface';
import {
  IThirdPartyApplications,
  IThirdPartyEcr,
  IThirdPartyEcrData,
  IThirdPartyLineOfBusiness,
  IThirdPartyMultipleLocations
} from '@ruby/modules/third-party/models/third-party.interface';
import { ThirdPartyEcrContainerComponent } from '@ruby/shared/dialogs/third-party/container/third-party-ecr/third-party-ecr.container';
import { ThirdPartyLocationsContainerComponent } from '@ruby/shared/dialogs/third-party/container/third-party-locations/third-party-locations.container';
import { ERole } from '@ruby/shared/enums/roles.enums';
import { UtilsService } from '@ruby/shared/services/utils/utils.service';

@Component({
  selector: 'ng-third-party-authorization',
  templateUrl: './third-authorization.component.html',
  styleUrls: ['./third-authorization.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ThirdPartyAuthorizationsComponent implements OnChanges {
  @Input() locationsByRole?: IThirdPartyMultipleLocations;
  @Input() ecrData?: IThirdPartyEcrData;
  @Input() lineOfBusinessList!: Array<IThirdPartyLineOfBusiness>;
  @Input() loadingLOB!: boolean;
  @Input() lineOfBusiness?: string;
  @Input() isEdit!: boolean;
  @Input() isNewLob!: boolean;
  @Input() apTransp!: boolean;
  @Input() apEcr!: boolean;
  @Input() apHljs!: boolean;
  @Input() apRating!: boolean;
  @Input() apRevenue!: boolean;
  @Input() apRevenueClaims!: boolean;
  @Input() apStorageAndLift!: boolean;
  @Output() selectedEcr = new EventEmitter<boolean>();
  @Output() selectedTransp = new EventEmitter<boolean>();
  @Output() selectedLob = new EventEmitter<ERole>();
  @Output() selectedRating = new EventEmitter<boolean>();
  @Output() selectedStorageAndLift = new EventEmitter<boolean>();
  @Output() selectedRevenue = new EventEmitter<boolean>();
  @Output() selectedRevenueClaims = new EventEmitter<boolean>();
  @Output() selectedHljs = new EventEmitter<boolean>();
  @Output() authStatus = new EventEmitter<boolean>();
  @ViewChild('swalConfirm') warningAlert!: SwalComponent;
  applications: IThirdPartyApplications = {
    hasStorageAndLift: true,
    hasInventoryManagement: true,
    hasHljs: true,
    hasECR: true,
    hasTransportation: true,
    hasRating: true,
    hasRevenue: true,
    hasRevenueClaims: true
  };
  authorizationForm: UntypedFormGroup = new UntypedFormGroup({
    transportation: new UntypedFormControl('', Validators.required),
    emptyCarRequest: new UntypedFormControl('', Validators.required),
    storageAndLift: new UntypedFormControl('', Validators.required),
    hljs: new UntypedFormControl('', Validators.required),
    rating: new UntypedFormControl('', Validators.required),
    revenue: new UntypedFormControl('', Validators.required),
    revenueClaims: new UntypedFormControl(false, Validators.required)
  });
  selectedLineOfBusiness?: ERole;
  imRole = ERole.ROLE_IM;
  offlineRole = ERole.ROLE_OFFLINE;
  selectedCustomer?: ICustomer;
  ecrDetails?: IThirdPartyEcr;
  displayLocations = false;

  constructor(private dialogSvc: MatDialog, private utilsSvc: UtilsService) { }

  get oneValidApplication(): boolean {
    const isFormValid: boolean = this.authorizationForm.controls.transportation.value ||
      this.authorizationForm.controls.emptyCarRequest.value ||
      this.authorizationForm.controls.storageAndLift.value ||
      this.authorizationForm.controls.hljs.value ||
      this.authorizationForm.controls.rating.value ||
      this.authorizationForm.controls.revenue.value ||
      this.authorizationForm.controls.revenueClaims.value;
    this.authStatus.emit(isFormValid);
    return isFormValid;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.lineOfBusinessList && changes.lineOfBusinessList.currentValue.length > 0) {
      this.setupApps();
    }

    if (changes.locationsByRole && changes.locationsByRole.currentValue && this.selectedLineOfBusiness) {
      this.selectedCustomer = changes.locationsByRole.currentValue[this.utilsSvc.getTypeRoleMapping(this.selectedLineOfBusiness)].customer;
      this.displayLocations = true;
    }

    if (changes.ecrData && changes.ecrData.currentValue) {
      this.ecrDetails = this.getEcrData(changes.ecrData.currentValue);
    }

    if (this.isEdit || this.isNewLob) {
      this.authorizationForm.controls.transportation.setValue(this.apTransp);
      this.authorizationForm.controls.emptyCarRequest.setValue(this.isEdit && !this.ecrDetails ? false : this.apEcr);
      this.authorizationForm.controls.hljs.setValue(this.apHljs);
      this.authorizationForm.controls.rating.setValue(this.apRating);
      this.authorizationForm.controls.revenue.setValue(this.apRevenue);
      this.authorizationForm.controls.revenueClaims.setValue(this.apRevenueClaims);
      this.authorizationForm.controls.storageAndLift.setValue(this.apStorageAndLift);
      if (this.lineOfBusiness) {
        this.selectedLineOfBusiness = this.lineOfBusiness as ERole;
        this.configureApps();
      }
    }
  }

  /**
   * Setup Apps
   *
   * @summary The following method setups the initial state of the applications regarding line of business
   * @returns void
   */
  setupApps(): void {
    this.selectedLineOfBusiness = this.lineOfBusinessList[0].role;
    if (this.selectedLineOfBusiness !== ERole.ROLE_IM && this.selectedLineOfBusiness !== ERole.ROLE_Dray) {
      this.applications.hasStorageAndLift = false;
    }
    if (this.selectedLineOfBusiness !== ERole.ROLE_SL) {
      this.applications.hasHljs = false;
    }
    if (this.selectedLineOfBusiness === ERole.ROLE_IM || this.selectedLineOfBusiness === ERole.ROLE_Dray) {
      this.applications.hasECR = false;
    }
    this.applications.hasTransportation = true;
    this.selectedLob.emit(this.selectedLineOfBusiness);
    this.authorizationForm.controls.transportation.setValue((this.selectedLineOfBusiness === this.imRole ||
      this.selectedLineOfBusiness === this.offlineRole));
  }

  /**
   * Configure Apps
   *
   * @summary The following method configure the visibility of the applications
   * @returns void
   */
  configureApps(): void {
    if (this.selectedLineOfBusiness === ERole.ROLE_IM || this.selectedLineOfBusiness === ERole.ROLE_Dray) {
      this.applications.hasStorageAndLift = true;
      this.applications.hasECR = false;
    } else {
      this.applications.hasStorageAndLift = false;
      this.applications.hasECR = true;
    }
    if (this.applications.hasHljs && this.selectedLineOfBusiness !== ERole.ROLE_SL) {
      this.applications.hasHljs = false;
    } else if (this.selectedLineOfBusiness === ERole.ROLE_SL) {
      this.applications.hasHljs = true;
    }
  }

  /**
   * Line Of Business Rules
   *
   * @summary The following method setups the state of the applications regarding line of business
   * @param $option: MatRadioChange
   * @returns void
   */
  lineOfBusinessRules($option: MatRadioChange): void {
    this.selectedLineOfBusiness = $option.value;
    this.configureApps();
    if (this.locationsByRole && this.selectedLineOfBusiness) {
      const obj: { customer: ICustomer } = this.locationsByRole[this.utilsSvc.getTypeRoleMapping(this.selectedLineOfBusiness)];
      this.authorizationForm.controls.transportation.setValue(obj ? obj.customer.locations.length > 0 : false);
    }
    this.selectedLob.emit(this.selectedLineOfBusiness);
  }

  /**
   * Display Rating Confirmation
   *
   * @summary The following method triggers when user selects rating application
   * @returns void
   */
  displayRatingConfirmation(): void {
    if (this.authorizationForm.controls.rating.value) {
      this.warningAlert.fire();
    } else {
      this.selectedRating.emit(this.authorizationForm.controls.rating.value);
    }
  }

  /**
   * Accept Rating
   *
   * @summary The following method sets the value of th rating app when user accepts
   * @returns void
   */
  acceptRating(): void {
    this.selectedRating.emit(this.authorizationForm.controls.rating.value);
  }

  /**
   * Cancel Rating
   *
   * @summary The following method change the slide toggle to it's correct value depending on form value
   * @returns void
   */
  cancelRating(): void {
    this.authorizationForm.controls.rating.setValue(false);
    this.selectedRating.emit(this.authorizationForm.controls.rating.value);
  }

  /**
   * Handle Locations Dialog
   *
   * @summary The following method checks whether open or not open a dialog
   * @param isEdit: boolean
   * @returns void
   */
  handleLocationsDialog(isEdit: boolean): void {
    if (this.selectedLineOfBusiness && !(this.selectedLineOfBusiness === ERole.ROLE_IM || this.selectedLineOfBusiness === ERole.ROLE_OFFLINE)) {
      if (this.locationsByRole) {
        const obj: { customer: ICustomer } = this.locationsByRole[this.utilsSvc.getTypeRoleMapping(this.selectedLineOfBusiness)];
        if (obj && isEdit) {
          this.openLocationsDialog(obj.customer);
          this.displayLocations = false;
        } else if (this.authorizationForm.controls.transportation.value && !obj) {
          this.openLocationsDialog();
          this.displayLocations = false;
        }
      } else if (this.authorizationForm.controls.transportation.value) {
        this.openLocationsDialog();
        this.displayLocations = false;
      }
    }
    this.selectedTransp.emit(this.authorizationForm.controls.transportation.value);
  }

  /**
   * Select Storage And Lift
   *
   * @summary The following method emits a boolean to change the status of Storage And Lift application
   * @param $event: MatSlideToggleChange
   * @returns void
   */
  selectStorageAndLift($event: MatSlideToggleChange): void {
    this.selectedStorageAndLift.emit($event.checked);
  }

  /**
   * Select Revenue
   *
   * @summary The following method emits a boolean to change the status of Revenue application
   * @param $event: MatSlideToggleChange
   * @returns void
   */
  selectRevenue($event: MatSlideToggleChange): void {
    this.selectedRevenue.emit($event.checked);
  }

  /**
   * Select HLJS
   *
   * @summary The following method emits a boolean to change the status of HLJS application
   * @param $event: MatSlideToggleChange
   * @returns void
   */
  selectHljs($event: MatSlideToggleChange): void {
    this.selectedHljs.emit($event.checked);
  }

  /**
   * Select Revenue Claims
   *
   * @summary The following method emits a boolean to change the status of Revenue Claims application
   * @param $event: MatSlideToggleChange
   * @returns void
   */
  selectRevenueClaims($event: MatSlideToggleChange): void {
    this.selectedRevenueClaims.emit($event.checked);
  }

  /**
   * Handle Ecr
   *
   * @summary The following method open the ecr dialog if there is ecrData or isEdit is true
   * @param isEdit: boolean
   * @returns void
   */
  handleEcr(isEdit: boolean): void {
    if ((!this.ecrData && !isEdit) || (this.ecrData && isEdit)) {
      this.openEcrDialog();
    }
    this.selectedEcr.emit(!!this.authorizationForm.controls.emptyCarRequest.value);
  }

  private openEcrDialog(): void {
    const dialogRef: MatDialogRef<ThirdPartyEcrContainerComponent> = this.dialogSvc.open(ThirdPartyEcrContainerComponent, {
      width: '50%',
      height: '74%',
      data: this.ecrData
    });
    dialogRef.afterClosed().pipe(take(1)).subscribe((isClosed: boolean) => {
        this.authorizationForm.controls.emptyCarRequest.setValue((this.ecrDetails && !isClosed) ? !!this.ecrDetails : isClosed);
        this.selectedEcr.emit(!!this.authorizationForm.controls.emptyCarRequest.value);
      }
    );
  }

  private openLocationsDialog(customerLoc?: ICustomer): void {
    const dialogRef: MatDialogRef<ThirdPartyLocationsContainerComponent> = this.dialogSvc.open(ThirdPartyLocationsContainerComponent, {
      width: '50%',
      height: '74%',
      data: {
        role: this.selectedLineOfBusiness,
        customer: customerLoc
      }
    });
    dialogRef.afterClosed().pipe(take(1)).subscribe((isClosed: boolean) => {
      if (this.locationsByRole && this.selectedLineOfBusiness) {
        const obj: { customer: ICustomer } = this.locationsByRole[this.utilsSvc.getTypeRoleMapping(this.selectedLineOfBusiness)];
        this.authorizationForm.controls.transportation.setValue(!!obj);
      } else {
        this.authorizationForm.controls.transportation.setValue(isClosed);
      }
      this.displayLocations = true;
      this.selectedTransp.emit(this.authorizationForm.controls.transportation.value);
    });
  }

  private getEcrData(ecr: IThirdPartyEcrData): IThirdPartyEcr {
    return {
      equipmentTypes: ecr.equipmentTypes,
      railRoadCompanyEId: ecr.railRoadCompany.key,
      railRoadLocState: ecr.originState,
      railRoadLocCity: ecr.originCity.key,
      stcc: ecr.stccDescription.key
    };
  }
}
